// src/pages/ADEdit.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  InputAdornment,
  FormControlLabel,
  Switch,
  Paper,
  Chip,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { CalendarToday } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";
import { useParams } from "react-router-dom";
const baseUrl = config.baseURL;

const AdEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [adLoading, setAdLoading] = useState(true);
  const [error, setError] = useState("");
  const [zones, setZones] = useState([]);
  const [advertiserId, setAdvertiserId] = useState(null);
  const [existingMedia, setExistingMedia] = useState([]);

  const token = localStorage.getItem("token");

  const [form, setForm] = useState({
    title: "",
    description: "",
    media_file: [],
    call_to_action: "link",
    call_to_action_value: "",
    zones: [],
    status: true,
    targeting_industries: "",
    targeting_categories: "",
    bid_price: "",
    max_budget: "",
    device_target: "all",
    start_date: "",
    end_date: "",
    retargeting_override: false,
  });
  // decode token
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setAdvertiserId(decoded.advertiserId || null);
    } catch (err) {
      console.error("Invalid token", err);
    }
  }, [token]);

  // fetch zones
  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/zone`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("res zones", res);
        setZones(res.data.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchZones();
  }, [token]);

  // fetch ad details
  useEffect(() => {
    console.log("id", id);
    if (!id) return;
    const fetchAd = async () => {
      try {
        const res = await axios.get(`${baseUrl}/api/ads/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const ad = res.data;
        console.log("ads edit ", ad);
        console.log("ads edit zones ", ad.zones);
        setForm({
          title: ad.title || "",
          description: ad.description || "",

          call_to_action: ad.call_to_action || "link",
          call_to_action_value:
            ad.call_to_action === "whatsapp" && ad.call_to_action_value
              ? ad.call_to_action_value.replace(/^https?:\/\/wa\.me\//, "")
              : ad.call_to_action_value || "",
          zones: ad.zones || [],
          status: ad.status === "active",
          targeting_industries: ad.targeting_industries
            ? Array.isArray(ad.targeting_industries)
              ? ad.targeting_industries
              : JSON.parse(ad.targeting_industries)
            : [],
          targeting_categories: ad.targeting_categories
            ? Array.isArray(ad.targeting_categories)
              ? ad.targeting_categories
              : JSON.parse(ad.targeting_categories)
            : [],
          bid_price: ad.bid_price || "",
          max_budget: ad.max_budget || "",
          device_target: ad.device_target || "all",
          start_date: ad.start_date ? ad.start_date.split("T")[0] : "",
          end_date: ad.end_date ? ad.end_date.split("T")[0] : "",

          retargeting_override: ad.retargeting_override || false,
        });
        setExistingMedia(
          ad.media_url
            ? Array.isArray(ad.media_url)
              ? ad.media_url
              : JSON.parse(ad.media_url) // parse stringified array
            : []
        );
      } catch (err) {
        console.error(err);
        setError("Failed to load ad for editing.");
      } finally {
        setAdLoading(false);
      }
    };
    fetchAd();
  }, [id, token]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === "media_file") {
      setForm((prev) => ({
        ...prev,
        media_file: Array.from(files),
      }));
    } else if (type === "checkbox") {
      setForm((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleZoneChange = (event) => {
    const {
      target: { value },
    } = event;
    setForm((prev) => ({
      ...prev,
      zone_id: Array.isArray(value)
        ? value.map((v) => Number(v))
        : [Number(value)],
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!advertiserId) return;

    setLoading(true);
    setError("");

    // Prevent updating active ad
    if (form.status) {
      toast.error("Cannot edit an active ad. Pause the ad first.");
      setLoading(false);
      return;
    }

    try {
      // Build payload
      const payload = {
        title: form.title || "",
        description: form.description || "",
        call_to_action: form.call_to_action || "",
        call_to_action_value: form.call_to_action_value || "",
        targeting_industries: Array.isArray(form.targeting_industries)
          ? form.targeting_industries
          : [],
        targeting_categories: Array.isArray(form.targeting_categories)
          ? form.targeting_categories
          : [],
        device_target: form.device_target || "all",
        start_date: form.start_date || "",
        end_date: form.end_date || "",
        bid_price: form.bid_price || "",
        max_budget: form.max_budget || "",
        zone_id: form.zones.map((z) => z.id),
        retargeting_override: form.retargeting_override ? 1 : 0,
      };

      // Convert payload into FormData
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // ✅ Always stringify arrays (zones, tags, industries, categories, etc.)
          formData.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      // Append media files if any
      const mediaFiles = form.media_file || [];
      console.log("mediaFiles", mediaFiles);

      mediaFiles.forEach((file, idx) => {
        formData.append("media_file[]", file); // ✅ use [] for multiple files (backend-friendly)
        console.log(`📎 Media file [${idx}]:`, file.name, file.type, file.size);
      });
      // (existingMedia || []).forEach((url) => {
      //   formData.append("existing_media[]", url);
      // });

      // Debug FormData properly
      for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(
            `🗂 ${key}: ${value.name} (${value.type}, ${value.size} bytes)`
          );
        } else {
          console.log(`📌 ${key}:`, value);
        }
      }
      // Send existing URLs (if not deleted by user)
      // Append existing media only if there are any
      //   if (existingMedia.length > 0) {
      //     existingMedia.forEach((url) =>
      //       formData.append("existing_media[]", url)
      //     );
      //   }
      console.log("payload", payload);
      for (let pair of formData.entries()) {
        console.log("formdat", pair[0], pair[1]);
      }
      // Send request
      const response = await axios.put(`${baseUrl}/api/ads/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Response:", response);
      if (response) {
        toast.success("Ad updated successfully!");
        navigate("/dashboard/Advertiser/ads");
      }
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.error || "Failed to update ad.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (adLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  //   const updateStatus = async (newStatus) => {
  //     try {
  //       await axios.patch(
  //         `${baseUrl}/api/ads/${id}/status`,
  //         { status: newStatus },
  //         { headers: { Authorization: `Bearer ${token}` } }
  //       );
  //       toast.success(`Ad ${newStatus === "active" ? "activated" : "paused"}!`);
  //       setForm((prev) => ({ ...prev, status: newStatus === "active" }));
  //     } catch (err) {
  //       console.error(err);
  //       toast.error(err.response?.data?.message || "Failed to update status");
  //     }
  //   };

  if (!advertiserId)
    return (
      <Box sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          You must be an advertiser to edit ads.
        </Typography>
      </Box>
    );

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 5, px: 2 }}>
      <Typography variant="h4" mb={3}>
        Edit Ad
      </Typography>
      <ToastContainer />
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        {/* --- Ad Content --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Ad Content
          </Typography>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Title"
                name="title"
                fullWidth
                required
                value={form.title}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                name="description"
                fullWidth
                multiline
                rows={4}
                required
                value={form.description}
                onChange={handleChange}
              />
            </Grid>

            {/* Media Upload */}

            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                Upload Photos/Videos (max 10)
                <input
                  type="file"
                  name="media_file"
                  multiple
                  accept="image/*,video/*"
                  hidden
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setForm((prev) => {
                      const combined = [...(prev.media_file || []), ...files];
                      return {
                        ...prev,
                        media_file: combined.slice(0, 10), // ✅ limit max 10
                      };
                    });
                  }}
                />
              </Button>

              {/* Render existing media */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(existingMedia || []).map((url, idx) => (
                  <Box key={idx} sx={{ position: "relative" }}>
                    <img
                      src={url}
                      style={{ width: 80, height: 80, objectFit: "cover" }}
                      alt="Existing Media"
                    />
                    <Button
                      size="small"
                      onClick={() => {
                        setExistingMedia((prev) =>
                          prev.filter((_, i) => i !== idx)
                        );
                      }}
                      sx={{ position: "absolute", top: 0, right: 0 }}
                    >
                      X
                    </Button>
                  </Box>
                ))}

                {/* Render newly selected media */}
                {(form.media_file || []).map((file, idx) => (
                  <Box key={idx} sx={{ position: "relative" }}>
                    {file.type.startsWith("image/") ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{ width: 80, height: 80, objectFit: "cover" }}
                      />
                    ) : (
                      <video
                        src={URL.createObjectURL(file)}
                        style={{ width: 80, height: 80, objectFit: "cover" }}
                      />
                    )}
                    <Button
                      size="small"
                      onClick={() => {
                        setForm((prev) => ({
                          ...prev,
                          media_file: prev.media_file.filter(
                            (_, i) => i !== idx
                          ),
                        }));
                      }}
                      sx={{ position: "absolute", top: 0, right: 0 }}
                    >
                      X
                    </Button>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Zones */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Zones</InputLabel>
                <Select
                  multiple
                  value={form.zones?.map((z) => z.id) || []} // match IDs
                  onChange={(e) => {
                    const selectedIds = e.target.value;
                    const selectedZones = zones.filter((z) =>
                      selectedIds.includes(z.id)
                    );
                    setForm((prev) => ({ ...prev, zones: selectedZones }));
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {form.zones.map((zone) => (
                        <Chip key={zone.id} label={zone.name} />
                      ))}
                    </Box>
                  )}
                >
                  {zones.map((zone) => (
                    <MenuItem key={zone.id} value={zone.id}>
                      {zone.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
        {/* --- Schedule --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Schedule
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Start Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Date"
                name="start_date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
                value={form.start_date}
                onChange={handleChange}
                helperText="Start date cannot be before today"
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Date"
                name="end_date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: form.start_date
                    ? new Date(
                        new Date(form.start_date).getTime() +
                          3 * 24 * 60 * 60 * 1000
                      )
                        .toISOString()
                        .split("T")[0]
                    : undefined,
                }}
                value={form.end_date}
                onChange={handleChange}
                helperText="End date must be at least 3 days after start"
              />
            </Grid>

            {/* Status Switch */}
            {/* <Grid item xs={12} sm={4} sx={{ textAlign: "center" }}>
              <InputLabel>Status of the Ad</InputLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.status}
                    name="status"
                    onChange={(e) =>
                      updateStatus(e.target.checked ? "active" : "paused")
                    }
                    color="primary"
                  />
                }
                label={form.status ? "Active" : "Paused"}
              />
              <Typography variant="caption" display="block">
                Minimum 3 days duration
              </Typography>
            </Grid> */}
          </Grid>
        </Paper>

        {/* --- Budget --- */}

        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Budget
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {/* Bid Price */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Tooltip
                  title="Amount you pay per click, impression, or action."
                  placement="left"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, mr: 0.5, cursor: "pointer" }}
                  />
                </Tooltip>
                <InputLabel sx={{ mb: 0 }}>Bid Price</InputLabel>
              </Box>
              <TextField
                name="bid_price"
                type="number"
                fullWidth
                value={form.bid_price}
                onChange={handleChange}
              />
            </Grid>

            {/* Max Budget */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Tooltip
                  title="Total amount you are willing to spend for this campaign."
                  placement="left"
                  arrow
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, mr: 0.5, cursor: "pointer" }}
                  />
                </Tooltip>
                <InputLabel sx={{ mb: 0, cursor: "pointer" }}>
                  Max Budget
                </InputLabel>
              </Box>
              <TextField
                name="max_budget"
                type="number"
                fullWidth
                value={form.max_budget}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* --- CTA & Targeting & Status --- */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <Typography variant="h6" mb={2}>
            Call To Action & Targeting
          </Typography>

          <Grid container spacing={3}>
            {/* CTA Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>CTA Type</InputLabel>
                <Select
                  name="call_to_action"
                  value={form.call_to_action}
                  onChange={(e) => {
                    handleChange(e);
                    // Reset CTA value when type changes
                    setForm((prev) => ({ ...prev, call_to_action_value: "" }));
                  }}
                >
                  <MenuItem value="link">Link / Website</MenuItem>
                  <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  <MenuItem value="app">App</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* CTA Value */}
            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  form.call_to_action === "whatsapp"
                    ? "WhatsApp Number (with country code)"
                    : form.call_to_action === "link"
                    ? "Website URL"
                    : "App Link"
                }
                name="call_to_action_value"
                fullWidth
                value={form.call_to_action_value}
                onChange={handleChange}
                helperText={
                  form.call_to_action === "whatsapp"
                    ? "This number will be converted to a WhatsApp link (e.g., +201234567890)"
                    : form.call_to_action === "link"
                    ? "Enter a valid website URL"
                    : "Enter your App link"
                }
                error={
                  form.call_to_action === "whatsapp" &&
                  form.call_to_action_value &&
                  !/^\+?[1-9]\d{7,14}$/.test(form.call_to_action_value)
                }
              />
            </Grid>
            {/* Device Target */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Device Target</InputLabel>
                <Select
                  name="device_target"
                  value={form.device_target}
                  onChange={handleChange}
                >
                  <MenuItem value="all">All Devices</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="pc">PC</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Target Categories */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                freeSolo
                options={[]} // you can add predefined categories here if needed
                value={form.targeting_categories}
                onChange={(e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    targeting_categories: newValue,
                  }))
                }
                slotProps={{
                  chip: { color: "default", variant: "outlined" },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Categories"
                    helperText="Type and press Enter to add multiple categories"
                  />
                )}
              />
            </Grid>
            {/* Target Industries */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                multiple
                freeSolo
                options={[]} // you can add predefined industries here if needed
                value={form.targeting_industries}
                onChange={(e, newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    targeting_industries: newValue,
                  }))
                }
                slotProps={{
                  chip: { color: "default", variant: "outlined" },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target Industries"
                    helperText="Type and press Enter to add multiple industries"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
        {/* --- Submit + Cancel --- */}
        <Box
          sx={{
            mb: 5,
            display: "flex",
            justifyContent: "center",
            gap: 2, // space between buttons
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: "#2E4053", minWidth: 150 }}
          >
            {loading ? <CircularProgress size={24} /> : "Update Ad"}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            sx={{ minWidth: 150 }}
            onClick={() => navigate("/dashboard/Advertiser/AdDetails")}
          >
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AdEdit;
